import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Save, Plus, Trash2, CheckCircle2, XCircle, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const abs = (url) => (url && url.startsWith("/api/files/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

const empty = () => ({
  id: "", name: "", tagline: "", description: "", price: 0,
  original_price: null, category: "", size: "", image: "", images: [],
  badge: "", processing_time: "3-5 business days", stock_status: "in_stock",
});

const ProductEditor = ({ initial, token, onSaved, onDeleted, isNew }) => {
  const [p, setP] = useState(initial || empty());
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const onFilePicked = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/.test(f.type)) {
      toast.error("Please pick a PNG, JPG, WEBP or GIF.");
      e.target.value = "";
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8 MB.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      set("image", data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        const body = { ...p, price: parseFloat(p.price) || 0 };
        if (body.original_price === "" || body.original_price === null) delete body.original_price;
        else body.original_price = parseFloat(body.original_price);
        const { data } = await axios.post(`${API}/admin/products`, body, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Created ${data.name}`);
        onSaved(data);
      } else {
        const patch = { ...p, price: parseFloat(p.price) || 0 };
        if (patch.original_price === "" || patch.original_price === null) patch.original_price = null;
        else patch.original_price = parseFloat(patch.original_price);
        const { data } = await axios.patch(`${API}/admin/products/${p.id}`, patch, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Saved ${data.name}`);
        onSaved(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${p.name}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Deleted ${p.name}`);
      onDeleted(p.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const oos = p.stock_status === "out_of_stock";
  const Field = ({ label, children }) => (
    <div>
      <p className="font-mono text-[10px] tracking-[0.3em] text-off-white/50 mb-1">{label}</p>
      {children}
    </div>
  );
  const input = "w-full h-10 bg-off-white/[0.04] border border-off-white/20 focus:border-rose outline-none px-3 text-off-white text-sm transition-colors-only";

  return (
    <div className={`border p-5 bg-off-white/[0.02] ${oos ? "border-rose/40" : "border-off-white/10"}`} data-testid={`product-edit-${p.id || "new"}`}>
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] text-rose">// {isNew ? "NEW PRODUCT" : "EDIT PRODUCT"}</p>
          <h3 className="font-serif-display italic text-2xl text-off-white mt-1">{p.name || "Untitled"}</h3>
        </div>
        <button
          onClick={() => set("stock_status", oos ? "in_stock" : "out_of_stock")}
          className={`h-10 px-3 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] border transition-colors-only ${
            oos ? "border-rose text-rose" : "border-off-white/25 text-off-white/70 hover:border-rose hover:text-rose"
          }`}
          data-testid={`toggle-stock-${p.id || "new"}`}
        >
          {oos ? <><XCircle size={14} /> OUT OF STOCK</> : <><CheckCircle2 size={14} /> IN STOCK</>}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="ID (URL SLUG)">
          <input value={p.id} onChange={(e) => set("id", e.target.value)} className={input} disabled={!isNew} />
        </Field>
        <Field label="NAME">
          <input value={p.name} onChange={(e) => set("name", e.target.value)} className={input} />
        </Field>
        <Field label="TAGLINE">
          <input value={p.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className={input} />
        </Field>
        <Field label="CATEGORY (SLUG)">
          <input value={p.category || ""} onChange={(e) => set("category", e.target.value)} className={input} />
        </Field>
        <Field label="PRICE (USD)">
          <input type="number" step="0.01" value={p.price} onChange={(e) => set("price", e.target.value)} className={input} />
        </Field>
        <Field label="ORIGINAL PRICE (OPTIONAL — SHOWS STRIKETHROUGH)">
          <input type="number" step="0.01" value={p.original_price ?? ""} onChange={(e) => set("original_price", e.target.value)} className={input} />
        </Field>
        <Field label="SIZE (e.g. 12 oz)">
          <input value={p.size || ""} onChange={(e) => set("size", e.target.value)} className={input} />
        </Field>
        <Field label="BADGE (e.g. BEST SELLER)">
          <input value={p.badge || ""} onChange={(e) => set("badge", e.target.value)} className={input} />
        </Field>
        <Field label="PROCESSING TIME">
          <input value={p.processing_time || ""} onChange={(e) => set("processing_time", e.target.value)} placeholder="3-5 business days" className={input} data-testid={`processing-input-${p.id || "new"}`} />
        </Field>
        <Field label="MAIN IMAGE">
          <div className="space-y-2">
            {p.image && (
              <div className="border border-off-white/10 bg-off-white/[0.02] p-3 flex items-center gap-3">
                <img src={abs(p.image)} alt="preview" className="w-16 h-16 object-contain bg-charcoal" />
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-[10px] tracking-[0.25em] text-off-white/50 mb-1">CURRENT</p>
                  <p className="font-mono text-[10px] text-off-white/70 truncate" title={p.image}>{p.image}</p>
                </div>
                <button type="button" onClick={() => set("image", "")} className="font-mono text-[10px] tracking-[0.25em] text-off-white/50 hover:text-rose-deep transition-colors-only" data-testid={`clear-image-${p.id || "new"}`}>CLEAR</button>
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                ref={fileRef}
                onChange={onFilePicked}
                className="hidden"
                data-testid={`file-input-${p.id || "new"}`}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="h-10 px-3 border border-rose text-rose font-mono text-[10px] tracking-[0.25em] flex items-center gap-2 hover:bg-rose hover:text-charcoal transition-colors-only disabled:opacity-60"
                data-testid={`upload-photo-${p.id || "new"}`}
              >
                {uploading ? <><Loader2 size={12} className="animate-spin" /> UPLOADING…</> : <><Upload size={12} /> UPLOAD PHOTO</>}
              </button>
              <input
                value={p.image || ""}
                onChange={(e) => set("image", e.target.value)}
                className={input}
                placeholder="…or paste an image URL"
                data-testid={`image-url-${p.id || "new"}`}
              />
            </div>
          </div>
        </Field>
        <div className="md:col-span-2">
          <Field label="DESCRIPTION">
            <textarea value={p.description || ""} onChange={(e) => set("description", e.target.value)} rows={3} className="w-full bg-off-white/[0.04] border border-off-white/20 focus:border-rose outline-none px-3 py-2 text-off-white text-sm transition-colors-only" />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="ADDITIONAL IMAGE URLS (ONE PER LINE)">
            <textarea
              value={(p.images || []).join("\n")}
              onChange={(e) => set("images", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
              rows={2}
              className="w-full bg-off-white/[0.04] border border-off-white/20 focus:border-rose outline-none px-3 py-2 text-off-white text-sm font-mono transition-colors-only"
            />
          </Field>
        </div>
      </div>

      <div className="flex gap-2 mt-5">
        <button onClick={save} disabled={busy} className="h-11 px-4 bg-rose text-charcoal font-mono text-[11px] tracking-[0.25em] font-semibold hover:bg-off-white transition-colors-only flex items-center gap-2 disabled:opacity-60" data-testid={`save-product-${p.id || "new"}`}>
          <Save size={14} /> {busy ? "SAVING…" : "SAVE"}
        </button>
        {!isNew && (
          <button onClick={del} disabled={busy} className="h-11 px-4 border border-off-white/20 hover:border-rose-deep hover:text-rose-deep font-mono text-[11px] tracking-[0.25em] flex items-center gap-2 text-off-white/70 transition-colors-only disabled:opacity-60" data-testid={`delete-product-${p.id}`}>
            <Trash2 size={14} /> DELETE
          </button>
        )}
      </div>
    </div>
  );
};

const FragranceRow = ({ frag, line, token, onUpdated, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const oos = frag.stock_status === "out_of_stock";

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/fragrances/${line}/${frag.code}`,
        { stock_status: oos ? "in_stock" : "out_of_stock" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdated(line, data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete fragrance ${frag.code} · ${frag.name}?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/fragrances/${line}/${frag.code}`, { headers: { Authorization: `Bearer ${token}` } });
      onDeleted(line, frag.code);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <li className="border border-off-white/10 bg-off-white/[0.02] px-4 py-3 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-3">
          <span className="font-mono text-[11px] tracking-[0.2em] text-rose">{frag.code}</span>
          <span className={`text-sm ${oos ? "line-through text-off-white/50" : "text-off-white"}`}>{frag.name}</span>
        </div>
        <p className="text-off-white/50 text-xs mt-1 line-clamp-1">{frag.notes}</p>
      </div>
      <button
        onClick={toggle}
        disabled={busy}
        className={`shrink-0 h-9 px-3 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] border transition-colors-only disabled:opacity-60 ${
          oos ? "border-rose text-rose" : "border-off-white/25 text-off-white/70 hover:border-rose hover:text-rose"
        }`}
        data-testid={`toggle-frag-${line}-${frag.code}`}
      >
        {oos ? <><XCircle size={12} /> OUT</> : <><CheckCircle2 size={12} /> IN</>}
      </button>
      <button
        onClick={remove}
        disabled={busy}
        className="shrink-0 h-9 px-2 border border-off-white/20 hover:border-rose-deep hover:text-rose-deep text-off-white/60 transition-colors-only disabled:opacity-50"
        data-testid={`delete-frag-${line}-${frag.code}`}
        aria-label="Delete fragrance"
      >
        <Trash2 size={12} />
      </button>
    </li>
  );
};

const NewFragranceForm = ({ line, token, onCreated }) => {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/fragrances/${line}`,
        { code: code.trim(), name: name.trim(), notes: notes.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onCreated(line, data);
      setCode(""); setName(""); setNotes("");
      toast.success(`Added ${data.code} · ${data.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add fragrance");
    } finally { setBusy(false); }
  };

  const cls = "h-10 bg-off-white/[0.04] border border-off-white/20 focus:border-rose outline-none px-3 text-off-white text-sm transition-colors-only";
  return (
    <form onSubmit={submit} className="border border-rose/30 bg-rose/[0.04] p-4 mb-4" data-testid={`new-frag-form-${line}`}>
      <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3">// ADD FRAGRANCE · {line.toUpperCase()}</p>
      <div className="grid md:grid-cols-[100px_1fr_auto] gap-2 items-start">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="CODE" className={cls} data-testid={`new-frag-code-${line}`} />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Inspired by ..." className={cls} data-testid={`new-frag-name-${line}`} />
        <button type="submit" disabled={busy || !code.trim() || !name.trim()} className="h-10 px-4 bg-rose text-charcoal font-mono text-[10px] tracking-[0.25em] font-semibold hover:bg-off-white transition-colors-only disabled:opacity-60 flex items-center gap-2" data-testid={`new-frag-submit-${line}`}>
          <Plus size={12} /> {busy ? "…" : "ADD"}
        </button>
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Top: … · Heart: … · Base: …"
        rows={2}
        className="w-full mt-2 bg-off-white/[0.04] border border-off-white/20 focus:border-rose outline-none px-3 py-2 text-off-white text-sm transition-colors-only"
        data-testid={`new-frag-notes-${line}`}
      />
    </form>
  );
};

const AdminCatalog = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [fragrances, setFragrances] = useState({ her: [], him: [] });
  const [loading, setLoading] = useState(true);
  const [addingNew, setAddingNew] = useState(false);
  const [tab, setTab] = useState("products");

  const load = async () => {
    setLoading(true);
    try {
      const [p, f] = await Promise.all([
        axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/fragrances`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProducts(p.data); setFragrances(f.data);
    } catch (_e) { /* auth handled by parent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onProductSaved = (saved) => {
    setProducts((prev) => {
      const has = prev.find((x) => x.id === saved.id);
      return has ? prev.map((x) => (x.id === saved.id ? saved : x)) : [...prev, saved];
    });
    setAddingNew(false);
  };
  const onProductDeleted = (id) => setProducts((prev) => prev.filter((x) => x.id !== id));

  const onFragUpdated = (line, saved) => {
    setFragrances((prev) => ({
      ...prev,
      [line]: prev[line].map((f) => (f.code === saved.code ? saved : f)),
    }));
  };

  const onFragCreated = (line, saved) => {
    setFragrances((prev) => ({
      ...prev,
      [line]: [...prev[line], saved].sort((a, b) => a.code.localeCompare(b.code)),
    }));
  };

  const onFragDeleted = (line, code) => {
    setFragrances((prev) => ({
      ...prev,
      [line]: prev[line].filter((f) => f.code !== code),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 py-16 text-off-white/60">
        <Loader2 size={16} className="animate-spin text-rose" />
        <span className="font-mono text-[11px] tracking-[0.25em]">LOADING CATALOG…</span>
      </div>
    );
  }

  return (
    <div className="py-6" data-testid="admin-catalog">
      <div className="flex items-center gap-2 border-b border-off-white/10 mb-6">
        {[
          { key: "products", label: "PRODUCTS" },
          { key: "her", label: "FRAGRANCES · HER" },
          { key: "him", label: "FRAGRANCES · HIM" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`h-10 px-4 font-mono text-[10px] tracking-[0.25em] transition-colors-only ${
              tab === t.key ? "text-rose border-b-2 border-rose" : "text-off-white/50 hover:text-off-white"
            }`}
            data-testid={`catalog-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div className="space-y-4" data-testid="admin-products-list">
          {products.map((p) => (
            <ProductEditor key={p.id} initial={p} token={token} onSaved={onProductSaved} onDeleted={onProductDeleted} />
          ))}
          {addingNew ? (
            <ProductEditor isNew token={token} onSaved={onProductSaved} onDeleted={() => setAddingNew(false)} />
          ) : (
            <button
              onClick={() => setAddingNew(true)}
              className="w-full h-14 border border-dashed border-off-white/30 hover:border-rose hover:text-rose text-off-white/60 font-mono text-[11px] tracking-[0.25em] flex items-center justify-center gap-2 transition-colors-only"
              data-testid="add-new-product-btn"
            >
              <Plus size={14} /> ADD NEW PRODUCT
            </button>
          )}
        </div>
      )}

      {tab === "her" && (
        <div>
          <NewFragranceForm line="her" token={token} onCreated={onFragCreated} />
          <ul className="space-y-2" data-testid="admin-frag-her">
            {(fragrances.her || []).map((f) => (
              <FragranceRow key={`her-${f.code}`} frag={f} line="her" token={token} onUpdated={onFragUpdated} onDeleted={onFragDeleted} />
            ))}
          </ul>
        </div>
      )}

      {tab === "him" && (
        <div>
          <NewFragranceForm line="him" token={token} onCreated={onFragCreated} />
          <ul className="space-y-2" data-testid="admin-frag-him">
            {(fragrances.him || []).map((f) => (
              <FragranceRow key={`him-${f.code}`} frag={f} line="him" token={token} onUpdated={onFragUpdated} onDeleted={onFragDeleted} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AdminCatalog;
